import React from 'react';
import { connect } from 'react-redux';

import CardView from 'src/components/StandardCardView/StandardCardView';
import container from 'src/ServiceContainer';
import { ContainerPayload } from 'src/components/RightContainer/RightContainer.slice';
import { DispatchProps, IdentityPropsConfig, ValueProps } from 'src/components/StandardCardView/StandardCardView.types';
import { makePrintSensitive } from 'src/components/higherOrder/Print/PrintSenstive';
import SubheaderDropdown from 'src/components/Subheader/SubheaderDropdown';

import { setRightContainerPayload } from 'src/components/RightContainer/RightContainer.slice';
import { getProcessedData, getSummary, getFilteredFlatData } from './SummaryView.selectors';
import store from 'src/store';
import type { AppThunkDispatch } from 'src/store.types';
import type { AppState } from 'src/reducers';
import {
  receiveTenantConfig,
  receiveError,
  requestTenantConfig,
  toggleSelected,
  clearSelection,
} from './SummaryView.slice';
import { TenantConfigViewData } from 'src/dao/tenantConfigClient';
import { getLocalConfig } from 'src/components/Subheader/Favorites/Favorites.utils';
import { retrieveIdentityPropsConfig } from 'src/components/StandardCardView/StandardCardView.utils';
import { getfabProps, withFab } from 'src/components/higherOrder/withFab';
import { makePopoverSensitive } from 'src/components/AssortmentStyleDetailsPopover/AssortmentStyleDetailsPopover';
import { flow, get, isEmpty, isNil } from 'lodash';
import { FavoriteListItemStorage } from 'src/components/Subheader/Favorites/Favorites.types';
import { isDataLoaded } from 'src/services/pivotServiceCache';
import { ComponentErrorType } from 'src/components/ErrorBoundary/ErrorBoundary.slice';
import { ConfDefnComponentType } from 'configuration-schemas/confdefnComponents';
import { AdornmentType } from 'configuration-schemas/viewdefns/literals';
import { AppType } from 'configuration-schemas/general';
import { SummaryViewOwnProps } from 'configuration-schemas/ownProps';
import { BackgroundDataLoadingProps } from 'src/components/BackgroundDataLoading/BackgroundDataLoading';
import { FabType } from 'configuration-schemas/confdefnView';
import { zSummaryViewDefn } from 'configuration-schemas/viewdefns/viewdefn';
import { zRollupDefn } from 'configuration-schemas/viewdefns/general';
import { getActivePerspectiveWorklistConfigButtonsConfig } from 'src/pages/PerspectiveSelection/PerspectiveSelection.selectors';
import { parseFloorsetDropdownData } from 'src/components/ConfigurableGrid/utils/ConfigurableGrid.utils';
import { getCurrentFloorsetIndex } from 'src/pages/AssortmentBuild/FloorsetComparison/FloorsetComparison.selectors';
import { updateTopMemberSelection } from 'src/components/Subheader/Subheader.slice';

let onFloorsetDropdownChange = (_event: React.ChangeEvent<HTMLSelectElement>) => {};

function handleFloorsetDropdownChange(dispatch: AppThunkDispatch, event: React.ChangeEvent<HTMLSelectElement>) {
  const selectedIndex = Number(event.target.value);
  if (Number.isNaN(selectedIndex)) {
    return;
  }

  const { topMemberOptions } = store.getState().subheader;
  const selection = topMemberOptions?.[selectedIndex]?.id;
  if (!isNil(selection)) {
    dispatch(updateTopMemberSelection(selection));
  }
}

function mapStateToProps(state: AppState, ownProps: SummaryViewOwnProps): ValueProps & BackgroundDataLoadingProps {
  const { summaryView } = state.pages.hindsighting.styleColorReview;
  const { defns, fabType = FabType.none, allowWorklistFunctionality, floorsetApi } = ownProps;
  const loaded = !summaryView.tenantConfigLoading && isDataLoaded(summaryView.viewDataState);
  const adornments: AdornmentType[] = get(summaryView.viewDefn, 'adornments', []);
  const cardType = get(summaryView.unmodifiedViewDefn.main, 'cardType', undefined);
  const fabProps = getfabProps(state, fabType);
  const selectionDataIndex = get(summaryView.unmodifiedViewDefn.main, 'selectionDataIndex', undefined);
  const worklistConfigButtons = getActivePerspectiveWorklistConfigButtonsConfig(state);
  const { topMemberOptions } = state.subheader;
  const processedTopMemberOptions = topMemberOptions && floorsetApi ? parseFloorsetDropdownData(topMemberOptions) : [];
  const topMemberSelectionIndex = getCurrentFloorsetIndex(state);
  const shouldShowFloorsetDropdown = Boolean(floorsetApi) && !isEmpty(processedTopMemberOptions);
  const rangePickerEl = shouldShowFloorsetDropdown
    ? React.createElement(SubheaderDropdown, {
        label: 'Floorsets',
        options: processedTopMemberOptions,
        selection: !isNil(topMemberSelectionIndex) && topMemberSelectionIndex >= 0 ? topMemberSelectionIndex : 0,
        handleChangeOnDropdown: onFloorsetDropdownChange,
        dataQa: 'floor-sets',
      })
    : undefined;

  return {
    loaded,
    config: summaryView.viewDefn,
    sortBy: state.subheader.sortBy,
    groupBy: state.subheader.groupBy,
    subheaderViewDefns: defns.subheader,
    filteredStyles: getFilteredFlatData(state),
    groupedStyles: getProcessedData(state),
    summary: getSummary(state),
    identityPropsConfig: summaryView.identityPropsConfig,
    currentTab: state.perspective.activeTab,
    unmodifiedViewDefn: summaryView.unmodifiedViewDefn,
    adornments,
    fab: fabProps,
    downloadLink: ownProps.subheader?.downloadLink,
    viewDataState: summaryView.viewDataState,
    cardType,
    allowWorklistFunctionality,
    worklistConfigButtons,
    selectionDataIndex,
    rangePickerEl,
    errorCondition: ownProps.subheaderErrorText,
  };
}

function dispatchToProps(dispatch: AppThunkDispatch, ownProps: SummaryViewOwnProps): DispatchProps {
  const { defns } = ownProps;
  onFloorsetDropdownChange = (event) => handleFloorsetDropdownChange(dispatch, event);

  return {
    onShowView() {
      const { tenantConfigClient } = container;
      dispatch(requestTenantConfig());
      tenantConfigClient
        .getTenantViewDefnsWithFavorites({
          defnIds: defns.view,
          appName: AppType.Assortment,
          validationSchemas: [zSummaryViewDefn, zRollupDefn],
        })
        .then((resp: (TenantConfigViewData & { identityProps?: IdentityPropsConfig })[]) => {
          const unmodifiedViewDefn = resp[0];
          const localConfig: FavoriteListItemStorage | undefined = getLocalConfig(
            defns.view[0],
            (resp as any)[defns.view.length],
            dispatch,
            unmodifiedViewDefn,
          );
          const storedConfig = localConfig && localConfig.config ? localConfig.config : resp[0];
          const identityPropsConfig = retrieveIdentityPropsConfig(resp[0]);

          dispatch(
            receiveTenantConfig({
              viewDefn: storedConfig,
              calcViewDefn: resp[1],
              unmodifiedViewDefn: unmodifiedViewDefn,
              identityPropsConfig,
            }),
          );
        })
        .catch((error) =>
          dispatch(
            receiveError({
              type: ComponentErrorType.config,
              message: (error as Error)?.message,
              name: ConfDefnComponentType.summaryView,
              stack: (error as Error)?.stack,
              issues: error,
              defnId: error.defnId,
            }),
          ),
        );
    },
    showStylePane(item: ContainerPayload) {
      dispatch(setRightContainerPayload(item));
    },
    onItemClicked(item: ContainerPayload) {
      dispatch(setRightContainerPayload(item));
    },
    onSelectItem(styleItem: any) {
      dispatch((_dispatch, getState) => {
        const state = getState();
        const selectionDataIndex = get(
          state.pages.hindsighting.styleColorReview.summaryView.unmodifiedViewDefn,
          'main.selectionDataIndex',
          undefined,
        );

        if (!selectionDataIndex) {
          // selection not enabled for this view
          return;
        }

        const id = styleItem?.[selectionDataIndex];
        if (isNil(id)) return;

        _dispatch(toggleSelected(id));
      });
    },
    clearSelection() {
      dispatch(clearSelection());
    },
    onConfigUpdate(config: TenantConfigViewData) {
      dispatch(
        receiveTenantConfig({
          viewDefn: config,
        }),
      );
    },
  };
}

const wrappedView = flow(() => CardView, withFab, makePopoverSensitive, makePrintSensitive)();

export default connect(mapStateToProps, dispatchToProps)(wrappedView);
